/**
 * Бюджетът на ЧАКАНЕТО за всяка заявка — таблица ПО МАРШРУТ, не един общ
 * таймаут.
 *
 * Без бюджет (днешното положение: `axios.create({ baseURL })` и нищо друго)
 * заявката при спрян интернет виси до таймаута на ОПЕРАЦИОННАТА СИСТЕМА —
 * между 35 и 90 s въртележка, без съобщение и без начин сервитьорът да
 * разбере какво става.
 *
 * ОБЩ таймаут обаче е по-лошо от никакъв. Облакът чака до 210 s за закриване
 * с карта (`FISCAL_CARD_TIMEOUT` в BE `app/core/mqtt/fiscal_handler.py`),
 * защото от другата страна стои ЧОВЕК с карта пред терминала. Общ таймаут от
 * 15 или 30 s би обявявал за провалено плащане, което точно в този момент се
 * въвежда — а парите вече са изтеглени. Затова всеки ред тук е
 * `сървърният бюджет + 10 s`: клиентът се отказва САМО след като сървърът
 * се е отказал и вече знае изхода.
 *
 * Правилото при съмнение е еднопосочно: ПО-ГОЛЯМОТО число. По-дългото чакане
 * струва нерви; преждевременният отказ на картово плащане струва пари и бон.
 *
 * Огледало на BE (`app/core/mqtt/fiscal_handler.py`) — смени ли се там
 * константа, сменя се и редът тук:
 *   FISCAL_RESULT_TIMEOUT      20 s → 30 s   закриване в брой, служебни суми
 *   FISCAL_CARD_TIMEOUT       210 s → 240 s  закриване с карта или смесено
 *   FISCAL_REPORT_TIMEOUT      30 s → 40 s   X/Z, печат на справка, СТОРНО
 *   FISCAL_RECEIPT_COPY_TIMEOUT 45 s → 55 s  копие от КЛЕН, предварителна сметка
 *   FISCAL_FM_REPORT_TIMEOUT  180 s → 190 s  отчет от фискалната памет
 *   FISCAL_RESET_TIMEOUT       60 s          × брой каси, последователно → зачистването няма таван
 * и на BE `app/features/assistant/service.py`:
 *   ASK_DEADLINE_SECONDS      120 s → 130 s  въпрос към AI помощника
 */

/** Всеки GET по подразбиране. */
export const BUDGET_GET_MS = 8_000;
/** Всяка мутация по подразбиране. */
export const BUDGET_MUTATION_MS = 15_000;
/** Закриване / разделяне с плащане В БРОЙ (сървър 20 s). */
export const BUDGET_CLOSE_CASH_MS = 30_000;
/**
 * Закриване / разделяне с плащане С КАРТА или СМЕСЕНО (сървър 210 s).
 * ТОВА Е КАПАНЪТ: числото не е предпазливост, а времето, в което гостът
 * държи картата на терминала.
 */
export const BUDGET_CLOSE_CARD_MS = 240_000;
/**
 * Сторно и възстановяване на сметка (сървър `FISCAL_REPORT_TIMEOUT` = 30 s,
 * не 20 — виж `publish_fiscal_storno`: кредитното известие минава през
 * preflight и пълен цикъл на бон).
 */
export const BUDGET_STORNO_MS = 40_000;
/** X отчет, Z отчет, печат на справка (сървър 30 s). */
export const BUDGET_REPORT_PRINT_MS = 40_000;
/** Служебно въвеждане и извеждане (сървър 20 s). */
export const BUDGET_CASH_OPERATION_MS = 30_000;
/** Отчет от фискалната памет за период (сървър 180 s). */
export const BUDGET_FM_REPORT_MS = 190_000;
/** Копие на бон от КЛЕН, предварителна сметка, печат на фактура (сървър 45 s). */
export const BUDGET_DOCUMENT_PRINT_MS = 55_000;
/** Тежки справки, глас, качване на изображения. */
export const BUDGET_HEAVY_MS = 60_000;
/**
 * Въпрос към AI помощника в „Мониторинг" (само служебният акаунт). Сървърът
 * спира целия въпрос на 120 s (`ASK_DEADLINE_SECONDS` в BE
 * `app/features/assistant/service.py`); обичайно отговорът е 6–20 s.
 */
export const BUDGET_ASSISTANT_MS = 130_000;
/** `POST /user/login` и `/user/login-with-code`. */
export const BUDGET_LOGIN_MS = 10_000;
/**
 * Зачистването на нулевите сметки: пренарежда номерата в базата и вика
 * `reset` по ВСЯКА каса ПОСЛЕДОВАТЕЛНО, по 60 s таван всяка
 * (`app/features/cleanup/service_tills.py::run_on_all_tills`). Три каси ×
 * две минавания = минути. Клиентски таймаут тук би прекъснал операцията по
 * средата на преномерирането — затова маршрутът остава практически без
 * таван, както е днес. Администраторска операция, пусната веднъж годишно.
 */
export const BUDGET_UNBOUNDED_MS = 600_000;

/** Методите без тяло — за тях важи бюджетът за четене. */
const READ_METHODS = new Set(["get", "head", "options"]);

/**
 * Четене ли е този метод. Освен бюджета, по него се решава и кое може да се
 * ПОВТОРИ срещу касата, когато режимът се смени под заявката
 * (`api/client.ts` §Повторение): четенето е безобидно, мутацията — не.
 */
export function isReadMethod(method: string | undefined): boolean {
  return READ_METHODS.has((method || "get").toLowerCase());
}

/** Методите на плащане, при които се чака ЧОВЕК на картовия терминал.
 * Огледало на `fiscal_handler.py`: `card_share > 0` само при 2 (карта) и
 * картовата половина на 3 (смесено). */
const CARD_PAYMENT_METHOD_IDS = new Set([2, 3]);

/**
 * Пътят на заявката, сведен до вида, по който се сравнява: без хост, без
 * query, без префикса на касата.
 *
 * В локален режим същите пътища отиват към касата под `…:8787/edge` — и там
 * работата е СЪЩАТА (касата печата същия бон на същия принтер), затова
 * бюджетът е същият. Отрязва се само префиксът, за да съвпаднат правилата.
 */
export function requestPath(url: string | undefined): string {
  let path = url ?? "";
  const scheme = path.indexOf("://");
  if (scheme !== -1) {
    const afterHost = path.indexOf("/", scheme + 3);
    path = afterHost === -1 ? "/" : path.slice(afterHost);
  }
  const cut = path.search(/[?#]/);
  if (cut !== -1) path = path.slice(0, cut);
  if (!path.startsWith("/")) path = `/${path}`;
  if (path === "/edge") return "/";
  if (path.startsWith("/edge/")) path = path.slice("/edge".length);
  return path.toLowerCase();
}

/** Числото от тялото, каквото и да е то: обект, вече сериализиран JSON низ
 * или нищо. `null` = не можем да разчетем — извикващият взима по-голямото. */
function readPaymentMethodId(data: unknown): number | null {
  const body = parseBody(data);
  if (!body) return null;
  const raw =
    body["paymentMethodId"] ?? body["payment_method_id"] ?? undefined;
  const id = typeof raw === "string" ? Number(raw) : raw;
  return typeof id === "number" && Number.isFinite(id) ? id : null;
}

function parseBody(data: unknown): Record<string, unknown> | null {
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  if (typeof data === "string" && data.startsWith("{")) {
    try {
      const parsed: unknown = JSON.parse(data);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      /* не е JSON — не знаем какво плащане е */
    }
  }
  return null;
}

/**
 * В брой или с карта. Решава се по ТЯЛОТО на заявката, защото само то знае
 * кой метод е избрал сервитьорът.
 *
 * Три случая дават картовия (дългия) бюджет:
 *  • метод 2 (карта) или 3 (смесено);
 *  • тяло със `mixedCardAmount` — смесено плащане, независимо как е кодирано;
 *  • НЕРАЗПОЗНАТО тяло. `POST /bills/{id}/close/retry` праща тяло БЕЗ
 *    `paymentMethodId`, когато повтаря документа с метода, записан по
 *    сметката (защита срещу дублиран бон) — тоест точно при повторния опит
 *    на картова сметка тук няма какво да се чете.
 */
export function closeBudgetFor(data: unknown): number {
  const body = parseBody(data);
  if (body && body["mixedCardAmount"] !== undefined) return BUDGET_CLOSE_CARD_MS;
  const id = readPaymentMethodId(data);
  if (id === null) return BUDGET_CLOSE_CARD_MS;
  return CARD_PAYMENT_METHOD_IDS.has(id)
    ? BUDGET_CLOSE_CARD_MS
    : BUDGET_CLOSE_CASH_MS;
}

/** Колко знака низ в тялото вече значат „това е файл, не поле". */
const UPLOAD_FIELD_CHARS = 2_000;
const UPLOAD_KEYS = [
  "image",
  "images",
  "logo",
  "banner",
  "photo",
  "avatar",
  "file",
  "audio",
];

/** Тяло, което носи файл (снимка на продукт, запис на глас). */
function looksLikeUpload(data: unknown): boolean {
  if (typeof FormData !== "undefined" && data instanceof FormData) return true;
  if (typeof Blob !== "undefined" && data instanceof Blob) return true;
  if (data instanceof ArrayBuffer) return true;
  if (typeof data === "string") return data.length > 100_000;
  const body = parseBody(data);
  if (!body) return false;
  return UPLOAD_KEYS.some((key) => {
    const value = body[key];
    return typeof value === "string" && value.length > UPLOAD_FIELD_CHARS;
  });
}

interface BudgetRule {
  /** Пътят, така както го връща `requestPath`. */
  readonly path: RegExp;
  /** Методите, за които важи правилото; липсва = всички. */
  readonly methods?: readonly string[];
  readonly budget: number | ((data: unknown) => number);
}

/**
 * Първото съвпадение печели, затова редът има значение: по-тесните маршрути
 * стоят преди по-широките (`/bills/{id}/close/retry` преди `/bills/…`).
 */
const RULES: readonly BudgetRule[] = [
  // Вход — и на екрана за вход има човек, който чака отговор.
  {
    path: /^\/user\/login(-with-code)?$/,
    methods: ["post"],
    budget: BUDGET_LOGIN_MS,
  },

  // Трите пътя, които издават ФИСКАЛЕН БОН за сметка. Само тук чакането
  // зависи от тялото.
  {
    path: /^\/bills\/[^/]+\/(close|close\/retry|split-and-pay)$/,
    methods: ["post"],
    budget: closeBudgetFor,
  },
  // Сторно: кредитно известие. Парите се връщат В БРОЙ от чекмеджето на
  // всяка марка (FiscalAPI, инвариант 13) — картов терминал не участва и
  // чакане от 240 s няма смисъл.
  //
  // `cancel-item` / `cancel-items` (сторно от Маркиране, ОТВОРЕНА сметка) НЕ
  // издават кредитно известие — тук до 0.0.274 пишеше обратното (BE
  // `service_cancel.cancel_bill_lines`: без фискален документ). Остават в
  // този ред, защото сървърът чака inline мониторите на всяка засегната
  // станция, а груповото сторно носи до 200 реда в една транзакция — 15 s
  // биха отказали клиента за сторно, което сървърът довършва.
  {
    path: /^\/bills\/[^/]+\/(cancel-item|cancel-items|cancel-all-items|restore|storno\/retry)$/,
    methods: ["post"],
    budget: BUDGET_STORNO_MS,
  },
  // Разделяне без плащане — нищо не се печата, но остава в семейството на
  // закриването: по-дългото чакане тук е безобидно.
  {
    path: /^\/bills\/[^/]+\/split$/,
    methods: ["post"],
    budget: BUDGET_CLOSE_CASH_MS,
  },
  // Документи, които излизат на хартия, докато човекът стои пред принтера.
  {
    path: /^\/bills\/[^/]+\/(receipt-copy|preliminary-bill)\/print$/,
    budget: BUDGET_DOCUMENT_PRINT_MS,
  },
  { path: /^\/invoices\/[^/]+\/print$/, budget: BUDGET_DOCUMENT_PRINT_MS },

  // Фискални операции.
  { path: /^\/operations\/fm-report$/, budget: BUDGET_FM_REPORT_MS },
  { path: /^\/operations\/(x-report|z-report)$/, budget: BUDGET_REPORT_PRINT_MS },
  { path: /^\/operations\/cash-(in|out)$/, budget: BUDGET_CASH_OPERATION_MS },
  {
    path: /^\/reports\/(personal|establishment)\/[^/]+\/print$/,
    budget: BUDGET_REPORT_PRINT_MS,
  },

  // Зачистването пипа базата и всяка каса — виж BUDGET_UNBOUNDED_MS.
  { path: /^\/cleanup\//, budget: BUDGET_UNBOUNDED_MS },
  // Отдалеченото обновяване на гейтуея: сървърът чака 20 s за „тръгна ли".
  {
    path: /^\/devices\/[^/]+\/fiscalapi\/update$/,
    budget: BUDGET_CLOSE_CASH_MS,
  },

  // AI помощникът: моделът мисли и чете данни на няколко стъпки.
  { path: /^\/assistant\/ask$/, methods: ["post"], budget: BUDGET_ASSISTANT_MS },
  // Тежко смятане на сървъра: справки, дневник, глас.
  { path: /^\/(reports|audit-logs|voice)(\/|$)/, budget: BUDGET_HEAVY_MS },
  // „История на маси" за период (`reportsBillsHistory.ts`) е справка, а не
  // четене на списък: на натоварен обект за месец тя минава над 8 s. Пътят ѝ
  // обаче е `/bills/history`, тоест правилото за справките не го хваща, а
  // подразбиращите се 8 s биха я отрязали точно там, където досега таван
  // нямаше изобщо — стеснение на поведението за всеки обект (инвариант 15).
  { path: /^\/bills\/history$/, budget: BUDGET_HEAVY_MS },
  {
    path: /^\/inventory\/(stock\/history|stock\/as-of|lots|stocktakes|report-postings|sold-|shortages)/,
    budget: BUDGET_HEAVY_MS,
  },
];

/**
 * Колко чака КЛИЕНТЪТ за тази заявка, в милисекунди.
 *
 * `method` е HTTP методът (регистърът няма значение), `url` е адресът така,
 * както го подава axios (относителен или пълен), `data` е тялото ПРЕДИ
 * сериализация — оттам се чете дали закриването е с карта.
 */
export function budgetFor(
  method: string,
  url: string,
  data?: unknown,
): number {
  const verb = (method || "get").toLowerCase();
  const path = requestPath(url);

  for (const rule of RULES) {
    if (rule.methods && !rule.methods.includes(verb)) continue;
    if (!rule.path.test(path)) continue;
    return typeof rule.budget === "function" ? rule.budget(data) : rule.budget;
  }

  if (isReadMethod(verb)) return BUDGET_GET_MS;
  if (looksLikeUpload(data)) return BUDGET_HEAVY_MS;
  return BUDGET_MUTATION_MS;
}
